import * as React from 'react';
import { Button, Modal, Spin, Form, Typography, Select, Space } from 'antd';
import { useEffect, useState } from 'react';
import { AuthApi } from '../../../apis/auth-api';
import { CountryPhoneNumberPrefix } from '../../../models/country-phone-number-prefix-interface';
import ReactCountryFlag from "react-country-flag";
import { UserPhoneAuthentication } from '../../../models/user-phone-authentication-interface';
import MaskedInput from 'antd-mask-input';
import { Logger } from '../../../utilites/loglySetup';
import { Profile } from '../../../models/profile-interface';
const { Text } = Typography;
const { Option } = Select;

interface LoginFormFields {
    UserName: string;
    Password: string;
    AuthenticationCode: string;
}

type Props = {
    setTwoFAProps: React.Dispatch<React.SetStateAction<{ maskedNumber: string, daysToRememberDevice: string }>>,
    isModalVisible: boolean,
    setIsModalVisible: React.Dispatch<React.SetStateAction<boolean>>,
    loginFields: LoginFormFields
    isLoading: boolean,
    setIsLoading: React.Dispatch<React.SetStateAction<boolean>>,
    setIs2FAModalVisible: React.Dispatch<React.SetStateAction<boolean>>,
    profile: Profile
};

const Register2FAModal: React.FC<Props> = ({
    setTwoFAProps,
    isModalVisible,
    setIsModalVisible,
    loginFields,
    isLoading,
    setIsLoading,
    setIs2FAModalVisible,
    profile
}) => {
    const [isBtnDisabled, setIsBtnDisabled] = useState<boolean>(true);
    const [countries, setCountries] = useState<CountryPhoneNumberPrefix[]>();
    const [phoneNumberFormat, setPhoneNumberFormat] = useState<string>('');
    const [form] = Form.useForm();

    useEffect(() => {
        AuthApi.get2FACountries()
            .then((countries) => {
                setCountries(countries);
                setPhoneNumberFormat(countries[0].phoneNumberFormat);
            }).catch((e) => {
                Logger().error(e)
            })
    }, []);

    /**
     * Cancel register 2fa form and return to login form
     */
    const handleCancel = () => {
        setIsModalVisible(false);
        form.resetFields();
    };

    /**
     * Initial register 2fa form submission for validation
     */
    const handleOk = async () => {
        try {
            await form.validateFields();
            setIsLoading(true);
        } catch (errorInfo) {
            Logger().log("ErrorInfo: ", errorInfo);
        }
    };

    /**
     * Register 2fa form submission
     * @param values `country`, `phoneNumber`
     */
    const onFinish = async (values: { country: number, phoneNumber: string }) => {
        const data: UserPhoneAuthentication = {
            username: loginFields.UserName,
            password: loginFields.Password,
            countryId: values.country,
            number: values.phoneNumber,
            authorizationCode: null,
            codeExpiration: null,
            registered: null,
            id: null,
            userId: null,
        };
        try {
            await AuthApi.post2FAUser(data);
            setIsModalVisible(false);
            const maskedNumber = phoneNumberFormat.slice(0, phoneNumberFormat.length - 2) + values.phoneNumber.slice(values.phoneNumber.length - 2);
            setTwoFAProps((prev) => ({ ...prev, maskedNumber: maskedNumber }));
            setIs2FAModalVisible(true);
        } catch (e) {
            Logger().error(e);
        }
        setIsLoading(false);
    };

    /**
     * Validation for 2fa code
     * @param _ 
     * @param value 
     * @returns promise
     */
    const checkCode = (_: any, value: string) => {
        const reg = /^\d+$/;
        if (!isNaN(parseInt(value.replace(/\(|\)|-|_| /g, ""))) && reg.test(value.replace(/\(|\)|-|_| /g, ""))) {
            return Promise.resolve();
        }

        return Promise.reject();
    }

    /**
     * Change country selection
     * @param value `countryId`
     */
    const handleCountryChange = (value: number) => {
        setPhoneNumberFormat(countries.find(country => country.id === value).phoneNumberFormat);
    }

    /**
     * Change phone number
     * @param e Html input
     */
    const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        // get country number length from phone number format
        const expectedPhoneNumberLength = (phoneNumberFormat.match(/\*/g) || []).length;

        // remove non numeric and get length
        const valueLength = e.target.value.replace(/\D/g, "").length;

        if (valueLength === expectedPhoneNumberLength)
            setIsBtnDisabled(false);
        else
            setIsBtnDisabled(true);
    }

    return (
        <Modal
            centered
            title="Two-Factor Authentication"
            visible={isModalVisible}
            onCancel={handleCancel}
            onOk={handleOk}
            getContainer={false}
            footer={[
                <Button id="CancelRegister2FAButton" key="back" onClick={handleCancel}>
                    Cancel
                </Button>,
                <Button id="Register2FAAddNumberButton" form="register2FAForm" key="submit" htmlType="submit" type="primary" loading={isLoading} onClick={handleOk} disabled={isBtnDisabled}>
                    Add Number
                </Button>
            ]}
        >
            <Spin spinning={isLoading}>
                <h3>Your Organization Requires Two-Factor Authentication</h3>
                <p>This is an added layer of security where you will be required to enter both a password and a code sent to your phone to gain access to your account.</p>
                <p>Now even if someone has your password it will not be enough to access your account.</p>
                <h4>What is your mobile phone number?</h4>

                <Form
                    layout="inline"
                    form={form}
                    id="register2FAForm"
                    onFinish={onFinish}
                    initialValues={{
                        'country': countries?.[0]?.id
                    }}
                >
                    <Form.Item name="country">
                        <Select onChange={handleCountryChange}>
                            {countries?.map((country, index) => {
                                return (
                                    <Option key={index} value={country.id}>
                                        <Space>
                                            <ReactCountryFlag
                                                countryCode={country.countryCode}
                                                svg
                                                title={country.countryCode}
                                            />
                                            <span>+{country.phoneNumberPrefix} </span>
                                            <span>{country.name}</span>
                                        </Space>

                                    </Option>
                                )
                            })}
                        </Select>
                    </Form.Item>
                    <Form.Item name="phoneNumber" rules={[{ required: true, message: 'Please input valid phone number', validator: checkCode }]}>
                        <MaskedInput mask={phoneNumberFormat.replace('*', '1')} placeholder={phoneNumberFormat} onChange={handlePhoneNumberChange} />
                    </Form.Item>
                </Form>

                <Text type="secondary">Number only used for security reasons.</Text>
                <br />
                <Text type="secondary">Message and data rates may apply.</Text>
            </Spin>
        </Modal>
    )
}

export default Register2FAModal;